import type { ErrorCode, TokenResponse } from '@et/shared';

/**
 * Typed fetch client for `/api/v1` (API_DOCS.md). Every non-2xx answer becomes an ApiError
 * carrying the envelope's code; a 401 clears the session so the app falls back to Login.
 * The server already returns money as strings — this client never converts them to numbers.
 */

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly status: number,
    readonly details?: { path: string; message: string }[],
  ) {
    super(message);
  }
}

const TOKEN_KEY = 'et.token';
const EXPIRES_KEY = 'et.expiresAt';

type Listener = () => void;
const listeners = new Set<Listener>();

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export const session = {
  token: (): string | null => {
    const s = storage();
    const token = s?.getItem(TOKEN_KEY) ?? null;
    const exp = s?.getItem(EXPIRES_KEY);
    if (token && exp && Date.parse(exp) <= Date.now()) {
      session.clear();
      return null;
    }
    return token;
  },
  expiresAt: (): number | null => {
    const exp = storage()?.getItem(EXPIRES_KEY);
    return exp ? Date.parse(exp) : null;
  },
  set: (t: TokenResponse): void => {
    storage()?.setItem(TOKEN_KEY, t.token);
    storage()?.setItem(EXPIRES_KEY, t.expiresAt);
    listeners.forEach((l) => {
      l();
    });
  },
  clear: (): void => {
    storage()?.removeItem(TOKEN_KEY);
    storage()?.removeItem(EXPIRES_KEY);
    listeners.forEach((l) => {
      l();
    });
  },
  subscribe: (l: Listener): (() => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  const token = session.token();
  if (token) headers['authorization'] = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method, headers, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  } catch {
    throw new ApiError('NETWORK', 'server unreachable', 0);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const env = (data as { error?: { code?: ErrorCode; message?: string; details?: { path: string; message: string }[] } } | null)?.error;
    if (res.status === 401 && path !== '/auth/login') session.clear();
    throw new ApiError(env?.code ?? 'INTERNAL', env?.message ?? `HTTP ${String(res.status)}`, res.status, env?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T = void>(path: string) => request<T>('DELETE', path),
};

/** Human message for an error, including field-level validation details. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const details = err.details?.map((d) => `${d.path}: ${d.message}`).join('; ');
    return details ? `${err.message} (${details})` : err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

/** Path segment for a unified symbol: "SOL/USDT" → "SOL-USDT" (the server accepts both). */
export const symbolPath = (symbol: string): string => symbol.replace('/', '-');
